// The Store: the treasury (gold balance; GET MORE GOLD shows the packs but can't sell them on the web), and the storefronts
// RESOURCES · SPEED UPS · TACTICS · CITY ADMIN with gold-price buttons that spend the gold the player already has.
// Every name, description, price, amount and duration comes from the server (village/get's store, iap/products for the
// gold packs) — nothing about the items is hard-coded here (Ben, Oct 14 2026), so a change on the server shows at once.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import type { IapProduct, StoreItem } from "@/api/types";
import { art } from "@/kit/art";
import { Countdown, ResMedal, ScreenHero, Stepper, TimeBar } from "@/kit/city";
import { IconClose } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Empty, Medallion, Row, RowText, Seg, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { BUILDING_NAMES, RESOURCE_KEYS, RESOURCE_NAMES, type ResourceKey } from "@/game/constants";
import { fmtNum } from "@/game/format";
import { fmtDuration, fmtSpan } from "@/game/time";
import { constructionQueue, effectExpiry, queueSlots, storefrontItems, type QueueEntry } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";
import { SpeedupSheet } from "./SpeedupSheet";
import { openTools, useVtOn } from "@/state/vt";

const FRONTS = ["resources", "speed_ups", "tactics", "city_admin"] as const;
type Front = (typeof FRONTS)[number];
const FRONT_LABELS: Record<Front, string> = { resources: "Resources", speed_ups: "Speed ups", tactics: "Tactics", city_admin: "City admin" };

/** "Lasts 15 days" from the item's duration (ms), for subscriptions. */
const lasts = (it: StoreItem) => {
  const ms = Number(it.duration) || 0;
  if (ms <= 0) return null;
  const n = (x: number, u: string) => `${+x.toFixed(1)} ${u}${x === 1 ? "" : "s"}`;
  return `Lasts ${ms % 86_400_000 === 0 ? n(ms / 86_400_000, "day") : ms >= 3_600_000 ? n(ms / 3_600_000, "hour") : n(ms / 60_000, "minute")}`;
};
/** The server's tagline, else its description. */
const blurb = (it: StoreItem) => it.tagline || it.description || "";
const nameStyle = { fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" } as const;

export function Store() {
  const { tab: tabP } = useParams();
  const [params, setParams] = useSearchParams();
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  const [packs, setPacks] = useState(false);
  const [trade, setTrade] = useState(params.get("trade") === "1");
  const [speedup, setSpeedup] = useState<QueueEntry | null>(null);
  const [confirm, setConfirm] = useState<StoreItem | null>(null);
  const [cancelSub, setCancelSub] = useState<StoreItem | null>(null);
  const tab = (FRONTS.includes(tabP as Front) ? tabP : "resources") as Front;
  const setTab = (t: Front) => nav(`${base}/store/${t}`, { replace: true });
  const queue = useMemo(() => (v ? constructionQueue(v) : []), [v]);
  if (!v) return <CityPage hud={false} header={{ strip: false }}>{null}</CityPage>;
  const gold = v.gold?.amount_owned ?? 0;
  const items = (front: string) => storefrontItems(v, front);

  const buy = (it: StoreItem, amount = 1) =>
    actions.run(`buy:${it.key}`, () => api.store.buy({ wid, vid }, it.key, amount), { success: it.type === 0 ? `${it.name} is active.` : `${it.name} bought.` });

  const onBuy = (it: StoreItem) => {
    if (it.effect === "truce") setConfirm(it);
    else void buy(it);
  };

  // A plain render function, not a component: a component declared inside Store would be a new type on every render
  // (the clock ticks each second), so React remounted every row and its <img> — the items flashed on iOS Safari.
  const itemRow = ({ it, note, rate }: { it: StoreItem; note?: React.ReactNode; rate?: React.ReactNode }) => {
    const sub = it.type === 0 ? lasts(it) : null;
    const exp = it.type === 0 ? effectExpiry(v, it.effect) : null;
    const active = !!exp && exp > now;
    const cooldown = it.available_at && it.available_at > now ? it.available_at : null;
    const owned = it.type === 1 ? it.amount_owned ?? 0 : 0;
    const disabled = gold < it.cost || (!!cooldown && !active) || (active && it.has_cooldown);
    return (
      <Row key={it.key} className={cx(active && "greenedge")} style={{ flexWrap: "wrap" }}>
        <Medallion src={art.store[it.key] ?? art.nav.store} alt="" size={72} glow={active ? "ember" : undefined} />
        <RowText
          line={<b className="creamt" style={nameStyle}>{it.name}</b>}
          meta={
            <span style={{ whiteSpace: "normal" }}>
              {blurb(it)}
              <br />
              {active ? <span className="c-active">Active <small>· <Countdown endMs={exp!} compact /></small></span>
                : cooldown ? <span className="muted">Available in {fmtSpan(cooldown - now)}</span>
                : owned ? <span className="gold">{fmtNum(owned)} owned</span>
                : rate ?? (sub ? <span className="muted">{sub}</span> : note)}
            </span>
          }
        />
        <div className="right" style={{ flexDirection: "column", alignItems: "flex-end", gap: "calc(var(--u) * 6)" }}>
          <button type="button" className="gold-price" disabled={disabled || actions.isBusy(`buy:${it.key}`)} onClick={() => onBuy(it)} aria-label={`Buy ${it.name} for ${it.cost} gold`}>
            <span className="coin" /> {fmtNum(it.cost)}
          </button>
          {active && it.cancellable && <button type="button" className="link" style={{ fontSize: "var(--fs-meta)" }} onClick={() => setCancelSub(it)}>Cancel</button>}
        </div>
      </Row>
    );
  };

  const boosters = items("resources").filter((it) => it.effect.startsWith("boost_"));
  const supplies = items("resources").filter((it) => !it.effect.startsWith("boost_") && it.key !== "npc_trade");
  const npc = items("resources").find((it) => it.key === "npc_trade") ?? v.store?.items?.npc_trade;
  const activeBoosts = boosters.filter((it) => (effectExpiry(v, it.effect) ?? 0) > now).length;
  const speedups = items("speed_ups");
  const tactics = items("tactics");
  const admin = items("city_admin");
  const perHour = (it: StoreItem) => (it.value ? (it.cost / (it.value / 3600)) : 0);
  const bestRate = speedups.length ? Math.min(...speedups.map(perHour)) : 0;

  return (
    <CityPage hud={false} header={{ strip: false }}>
      <div className="stack">
        {/* The world menu bar (World tab · Reports · Mail · city box · Announcements · menu) stays on the Store (Ben, Oct
            11 2026), without the resource strip; then ‹ STORE, "You own … gold" and Get more gold (Rev. 11 §7). */}
        <ScreenHero title="Store" onBack={() => nav(base)}>
          <div className="tstrip-act treasury-strip">
            <p><span className="lbl">You own</span><span className="amt"><img src={art.nav.store} alt="" className="coin" /><b className="gold">{fmtNum(gold)}</b> gold</span></p>
            <Button kind="crimson" size="md" onClick={() => setPacks(true)}>Get more gold</Button>
          </div>
        </ScreenHero>
        <Seg value={tab} onChange={setTab} options={FRONTS.map((f) => ({ value: f, label: v.store?.storefronts?.[f]?.title ?? FRONT_LABELS[f] }))} />

        {tab === "resources" && (
          <>
            <Card>
              <CardHead title="Supplies" aside={<>Delivered to <b className="gold">{v.village?.city_name}</b></>} />
              <div className="rows">
                {supplies.map((it) => itemRow({ it }))}
                {npc && (
                  <Row>
                    <Medallion src={art.store.npc_trade} alt="" size={72} />
                    <RowText line={<b className="creamt" style={nameStyle}>{npc.name}</b>} meta={<span style={{ whiteSpace: "normal" }}>{blurb(npc)}<br /><span className="muted">{fmtNum(npc.cost)} gold per {fmtNum(npc.value)}</span></span>} />
                    <div className="right"><Button kind="gold" size="md" disabled={gold < npc.cost} onClick={() => setTrade(true)}>Trade</Button></div>
                  </Row>
                )}
                {!supplies.length && !npc && <Empty>The game sent no supplies for sale.</Empty>}
              </div>
            </Card>
            <Card>
              <CardHead title="Boosters" aside={`${activeBoosts} of ${boosters.length} active`} />
              <div className="rows">{boosters.map((it) => itemRow({ it }))}{!boosters.length && <Empty>No boosters for sale.</Empty>}</div>
            </Card>
          </>
        )}

        {tab === "speed_ups" && (
          <>
            <Card>
              <CardHead title="Speed-ups" aside={<>Cut upgrade time in <b className="gold">{v.village?.city_name}</b></>} />
              <div className="rows">
                {speedups.map((it) => {
                  const r = perHour(it);
                  const best = r > 0 && r === bestRate;
                  return itemRow({ it, rate: <span className={best ? "gold" : "muted"}>{best ? "Best rate · " : ""}{r % 1 ? r.toFixed(1) : r} gold per hour{it.amount_owned ? ` · ${fmtNum(it.amount_owned)} owned` : ""}</span> });
                })}
                {!speedups.length && <Empty>No speed-ups for sale in this world.</Empty>}
              </div>
            </Card>
            <Card>
              <CardHead title="Build queue" aside={`${queue.length} of ${queueSlots(v)} slots in use`} />
              {!queue.length ? <Empty>Nothing under construction.</Empty> : (
                <div className="rows">
                  {queue.map((q, i) => (
                    <Row key={i} style={{ flexWrap: "wrap" }}>
                      <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".06em", textTransform: "uppercase" }}>{BUILDING_NAMES[q.item.key] ?? q.item.name}</b>} meta={<i>Level {q.item.level - 1} → {q.item.level}</i>} />
                      <div className="right">
                        <div className="val muted" style={{ font: "400 var(--fs-meta)/1 var(--text)" }}>{fmtDuration(Math.max(0, q.endMs - now), { compact: true })} left</div>
                        <Button size="sm" disabled={!!q.item.has_used_speedup} onClick={() => setSpeedup(q)}>Speed up</Button>
                      </div>
                      <div className="row-body"><TimeBar startMs={q.startMs} endMs={q.endMs} /></div>
                    </Row>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

        {tab === "tactics" && (
          <Card>
            <CardHead title="Tactics" aside={`${tactics.filter((it) => (effectExpiry(v, it.effect) ?? 0) > now).length} active`} />
            <div className="rows">{tactics.map((it) => itemRow({ it }))}{!tactics.length && <Empty>No tactics for sale.</Empty>}</div>
          </Card>
        )}

        {tab === "city_admin" && (
          // No featured card (Ben, Oct 14 2026): the queue extender is an ordinary row, first in the server's order
          <Card>
            <CardHead title={v.store?.storefronts?.city_admin?.title ?? FRONT_LABELS.city_admin} aside="Account-wide" />
            <div className="rows">{admin.map((it) => itemRow({ it }))}{!admin.length && <Empty>Nothing for sale here.</Empty>}</div>
          </Card>
        )}
      </div>

      <GoldPacksSheet open={packs} onClose={() => setPacks(false)} gold={gold} wid={wid} />
      <InstantTradeSheet open={trade} onClose={() => { setTrade(false); if (params.get("trade")) setParams({}, { replace: true }); }} />
      <SpeedupSheet job={speedup} onClose={() => setSpeedup(null)} />
      <Confirm open={!!confirm} onClose={() => setConfirm(null)} kind="gold" title={`Activate ${confirm?.name}?`}
        text={confirm ? `${confirm.name} stops all troop movements to and from your cities for ${fmtSpan(confirm.duration)} — yours too. Armies already marching keep going.` : ""}
        action={confirm ? `Activate · ${confirm.cost} gold` : ""} busy={confirm ? actions.isBusy(`buy:${confirm.key}`) : false}
        onConfirm={async () => { if (!confirm) return; const r = await buy(confirm); if (r) setConfirm(null); }} />
      <Confirm open={!!cancelSub} onClose={() => setCancelSub(null)} title={`End ${cancelSub?.name}?`} text="The effect ends now. Gold already spent is not returned." action="End it" busy={cancelSub ? actions.isBusy("cancel_sub") : false}
        onConfirm={async () => { if (!cancelSub) return; const r = await actions.run("cancel_sub", () => api.store.cancelSubscription({ wid, vid }, cancelSub.key), { success: `${cancelSub.name} ended.` }); if (r) setCancelSub(null); }} />
    </CityPage>
  );
}

/** The pack art by tier (0 Mini … 4 Supreme); a tier past the art falls back to the gold coin. */
const PACK_ART = ["mini", "basic", "standard", "large", "supreme"];

/** The gold packs as the server lists them (iap/products): title, gold (+ any bonus gold), price. The "+N% bonus gold" tag is
 *  each pack's gold per dollar against the cheapest pack's; "Best value" goes to the highest rate. */
export function goldPacks(products: IapProduct[]) {
  const rows = products
    .map((p) => ({ id: p.product_id, tier: Number(p.tier) || 0, title: p.title, gold: Number(p.gold) + Number(p.gold_bonus || 0), price: Number(p.price) }))
    .filter((p) => p.gold > 0)
    .sort((a, b) => a.tier - b.tier || a.price - b.price);
  const rate = (p: { gold: number; price: number }) => (p.price > 0 ? p.gold / p.price : 0);
  const base = rows.length ? rate(rows.reduce((m, p) => (p.price < m.price ? p : m))) : 0;
  const best = rows.length > 1 ? Math.max(...rows.map(rate)) : -1;
  return rows.map((p) => ({ ...p, bonus: base > 0 ? Math.max(0, Math.round((rate(p) / base - 1) * 100)) : 0, best: rate(p) === best && best > base }));
}

function GoldPacksSheet({ open, onClose, gold, wid }: { open: boolean; onClose: () => void; gold: number; wid: number }) {
  const q = useQuery({ queryKey: ["iap-products"], queryFn: () => api.iap.products(), enabled: open, staleTime: 10 * 60_000 });
  // Valor Tools switched on for the player (Oct 15 2026): gold is bought on Valor Tools' Store (Stripe), credited to this game account
  const vtOn = useVtOn(wid);
  const buyOnTools = () => openTools(wid, `/app/?tab=dashboard&dt=store&w=${wid}`, true);
  const packs = useMemo(() => goldPacks(q.data?.products ?? []), [q.data]);
  return (
    <Sheet open={open} onClose={onClose} label="Get more gold">
      <div className="packs-head">
        <h3>Get more gold</h3>
        <Disc size="sm" label="Close" onClick={onClose}><IconClose /></Disc>
      </div>
      <p className="muted center" style={{ margin: "0 0 14px", font: "400 var(--fs-body2)/1.3 var(--text)" }}>You own <b className="gold">{fmtNum(gold)}</b> gold</p>
      {vtOn && (
        <div className="vt-gold" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "calc(var(--u) * 8)", margin: "0 0 14px" }}>
          <Button kind="gold" wide onClick={buyOnTools}>Buy gold on Valor Tools</Button>
          <span className="muted center" style={{ font: "400 var(--fs-meta)/1.35 var(--text)" }}>Pay by card on Valor Tools' Store; the gold goes to this game account.</span>
        </div>
      )}
      <div className="rows">
        {packs.map((p) => (
          <Row key={p.id} className={cx("pack", p.best && "best")} style={{ alignItems: "stretch" }}>
            <img className="pack-art" src={PACK_ART[p.tier] ? art.pack(PACK_ART[p.tier]) : art.nav.store} alt="" />
            <div className="txt" style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "calc(var(--u) * 8)" }}>
              <div className="eyebrow">{p.title}</div>
              <div className="pack-gold"><span className="coin" /> {fmtNum(p.gold)} <small>gold</small></div>
              {p.bonus > 0 && <span className="pilltag green" style={{ alignSelf: "flex-start" }}>+{p.bonus}% bonus gold</span>}
            </div>
            <div className="right" style={{ flexDirection: "column", alignItems: "flex-end", justifyContent: "center", gap: "calc(var(--u) * 10)" }}>
              {p.best && <span className="ribbon">Best value</span>}
              <button type="button" className="gold-price lg" disabled title={vtOn ? "The Valor app's price: buy on Valor Tools above" : "Gold can't be bought on the web yet"}>${p.price.toFixed(2)}</button>
            </div>
          </Row>
        ))}
        {q.isLoading && <Empty>Loading the gold packs…</Empty>}
        {q.isError && <Empty>The gold packs couldn't be loaded.</Empty>}
        {q.isSuccess && !packs.length && <Empty>No gold packs listed.</Empty>}
      </div>
      <div className="c-note">{vtOn ? "These are the Valor app's packs and prices. Valor Tools' Store has its own prices and sales." : "Gold can't be bought on the web yet. Buy it in the Valor app and it appears here."}</div>
    </Sheet>
  );
}

function InstantTradeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { v, wid, vid, actions } = useCity();
  const [give, setGive] = useState<ResourceKey>("r_w");
  const [get, setGet] = useState<ResourceKey>("r_c");
  const [blocks, setBlocks] = useState(1);
  if (!v) return null;
  const item = v.store?.items?.npc_trade;
  const per = item?.value ?? 1000;
  const cost = item?.cost ?? 25;
  const gold = v.gold?.amount_owned ?? 0;
  const maxBlocks = Math.max(1, Math.min(Math.floor((v.resources?.[give] ?? 0) / per), Math.floor(gold / cost)));
  const n = Math.min(blocks, maxBlocks);
  const ok = !!item && give !== get && (v.resources?.[give] ?? 0) >= per * n && gold >= cost * n;
  return (
    <Sheet open={open} onClose={onClose} label="Instant trade" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Instant trade</h3>}>
      <div className="offer-grid">
        <span className="eyebrow">You give</span>
        <div className="picks">{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={give === r} onClick={() => setGive(r)} />)}</div>
        <span className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)" }}>{fmtNum((v.resources?.[give] ?? 0))} in city</span>
        <span className="eyebrow">You get</span>
        <div className="picks">{RESOURCE_KEYS.map((r) => <ResMedal key={r} r={r} size={64} selected={get === r} onClick={() => setGet(r)} />)}</div>
        <span />
      </div>
      <div className="offer-opts">
        <div><div className="eyebrow">Amount</div><div className="muted hint">{fmtNum(per)} per block · {cost} gold each</div></div>
        <Stepper value={n} min={1} max={maxBlocks} onChange={setBlocks} format={(b) => fmtNum(b * per)} label="Blocks" />
      </div>
      <Button kind="gold" wide style={{ marginTop: 14 }} disabled={!ok} busy={actions.isBusy("npc")}
        onClick={async () => { const r = await actions.run("npc", () => api.store.instantResourceTrade({ wid, vid }, "npc_trade", give, get, n), { success: `Traded ${fmtNum(per * n)} ${RESOURCE_NAMES[give].toLowerCase()} for ${RESOURCE_NAMES[get].toLowerCase()}.` }); if (r) onClose(); }}>
        Trade <span className="sep" /> <span className="coin" /> {fmtNum(cost * n)}
      </Button>
      {give === get && <div className="c-note">Pick two different resources.</div>}
    </Sheet>
  );
}
